import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post, put } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import type { Action, Dispatch, Note, NoteBrandListItem, ThunkAction } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));

const CREATED = 'notes/CREATED';
const LOADING = 'notes/LOADING';
const LOADED = 'notes/LOADED';
const UPDATED = 'notes/UPDATED';
const DELETED = 'notes/DELETED';
const RESET_NOTES = 'notes/RESET_NOTES';
const BRANDS_LOADED = 'notes/BRANDS_LOADED';
const RESET_BRANDS = 'notes/RESET_BRANDS';

const {
  getBrandsThatHaveAddedNotes,
  getNoteCreate,
  getNoteDelete,
  getNoteUpdate,
  getNotes
} = API.END_POINTS.API;

const initialState = {
  creatorList: [],
  campaignList: [],
  brandList: [],
  isLoading: false
};

export function getContext(campaignId?: number | null) {
  return campaignId ? 'campaign' : 'creator';
}

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

function notesLoaded(data: Array<Note>, context: string): Action {
  return {
    type: LOADED,
    payload: {
      context,
      data
    }
  };
}

function noteCreated(data: Note, context: string): Action {
  return {
    type: CREATED,
    payload: {
      context,
      data
    }
  };
}

function noteUpdated(data: Note, context: string): Action {
  return {
    type: UPDATED,
    payload: {
      context,
      data
    }
  };
}

function noteDeleted(id: number, context: string): Action {
  return {
    type: DELETED,
    payload: {
      context,
      id
    }
  };
}

function brandsLoaded(data: Array<NoteBrandListItem>): Action {
  return {
    type: BRANDS_LOADED,
    payload: {
      data
    }
  };
}

export function resetNotes(): Action {
  return {
    type: RESET_NOTES
  };
}

export function resetBrandsThatHaveAddedNotes(): Action {
  return {
    type: RESET_BRANDS,
    payload: {
      brands: []
    }
  };
}

export function fetchNotes(influencerId: number, filter: string, campaignId?: number | null): ThunkAction {
  return (dispatch: Dispatch, getState) => {
    const organizationId = getState().organizations.currentOrganizationId;

    if (!organizationId) {
      return Promise.resolve();
    }

    const url = getNotes(organizationId, influencerId, filter, campaignId);

    dispatch(setLoadingStatus(true));

    return get(url)
      .then((response) => {
        const context = getContext(campaignId);
        dispatch(notesLoaded(response.data, context));
      })
      .catch((error) => {
        dispatch(setLoadingStatus(false));
        logError(error);
      });
  };
}

export function addNote(text: string, influencerId: number, campaignId?: number | null): ThunkAction {
  return (dispatch: Dispatch, getState) => {
    const organizationId = getState().organizations.currentOrganizationId;

    if (!organizationId) {
      return Promise.resolve();
    }

    dispatch(setLoadingStatus(true));

    return post(getNoteCreate(organizationId), { data: { influencer_profile_id: influencerId, text, campaignId } })
      .then(response => {
        const context = getContext(campaignId);
        dispatch(noteCreated(response.data, context));
      })
      .catch(error => {
        dispatch(setLoadingStatus(false));
        dispatch(danger(__t('collabary_common.errors.unknown_error')));
        logError(error);
      });
  };
}

export function updateNote(text: string, influencerId: number, note: Note, campaignId?: number | null): ThunkAction {
  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    return put(getNoteUpdate(note.organizationId, note.id), { data: { influencer_profile_id: influencerId, text } })
      .then(response => {
        const context = getContext(campaignId);
        dispatch(noteUpdated(response.data, context));
      })
      .catch(error => {
        dispatch(setLoadingStatus(false));
        dispatch(danger(__t('collabary_common.errors.unknown_error')));
        logError(error);
      });
  };
}

export function deleteNote(influencerId: number, note: Note, campaignId?: number | null): ThunkAction {
  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    return destroy(getNoteDelete(note.organizationId, note.id))
      .then(() => {
        const context = getContext(campaignId);
        dispatch(noteDeleted(note.id, context));
      })
      .catch(error => {
        dispatch(setLoadingStatus(false));
        dispatch(danger(__t('collabary_common.errors.unknown_error')));
        logError(error);
      });
  };
}

export function fetchBrandsThatHaveAddedNotes(influencerId: number): ThunkAction {
  return (dispatch: Dispatch) => {
    const url = getBrandsThatHaveAddedNotes(influencerId);

    dispatch(setLoadingStatus(true));

    return get(url)
      .then((response) => {
        dispatch(brandsLoaded(response.data));
      })
      .catch((error) => {
        dispatch(setLoadingStatus(false));
        logError(error);
      });
  };
}

export default createReducer<any, any>('notes', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET_NOTES]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { data, context }) => ({
    ...state,
    [`${context}List`]: data,
    isLoading: false
  }),
  [CREATED]: (state, { data, context }) => ({
    ...state,
    [`${context}List`]: [data].concat(state[`${context}List`]),
    isLoading: false
  }),
  [UPDATED]: (state, { data, context }) => ({
    ...state,
    [`${context}List`]: state[`${context}List`].length > 0 ? (
      state[`${context}List`].map(item => {
        if (item.id === data.id) {
          return { isEdited: true, ...data };
        }
        return item;
      })
    ) : ([data]),
    isLoading: false
  }),
  [DELETED]: (state, { id, context }) => ({
    ...state,
    [`${context}List`]: state[`${context}List`].filter(note => id !== note.id),
    isLoading: false
  }),
  [BRANDS_LOADED]: (state, { data }) => ({
    ...state,
    brandList: data,
    isLoading: false
  }),
  [RESET_BRANDS]: (state) => ({
    ...state,
    brandList: []
  })
});
